/**
 * ReviewCard — Compact card for coach kinetic session review queue.
 *
 * Shows athlete info, exercise, rep count, confidence, video player.
 * Approve/Reject actions with rejection reason input.
 */

import { useState } from 'react';
import { supabase } from '@/supabase/client';

interface ReviewSession {
  id: string;
  user_id: string;
  tool_id: string;
  verified_reps: number;
  video_url: string | null;
  sealed_at: string;
  coach_review_status: string;
  metadata: Record<string, any>;
  exercise_name: string;
  metric_id: string | null;
  user: {
    id: string;
    full_name: string | null;
    avatar_url: string | null;
    role: string | null;
  } | null;
}

interface ReviewCardProps {
  session: ReviewSession;
  onApproved: (sessionId: string) => void;
  onRejected: (sessionId: string) => void;
  ktsUrl: string;
  token: string;
}

export default function ReviewCard({ session, onApproved, onRejected, ktsUrl, token }: ReviewCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);

  const name = session.user?.full_name || 'Athlete';
  const avatar = session.user?.avatar_url;
  const sealedDate = new Date(session.sealed_at).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });

  // Load signed video URL on expand
  const handleExpand = async () => {
    setExpanded(!expanded);
    if (!expanded && session.video_url && !videoUrl) {
      try {
        const { data } = await supabase.storage
          .from('prospects-media')
          .createSignedUrl(session.video_url, 600);
        if (data?.signedUrl) setVideoUrl(data.signedUrl);
      } catch {
        // Try direct URL as fallback
        setVideoUrl(session.video_url);
      }
    }
  };

  const handleApprove = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${ktsUrl}/api/review/${session.id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Approval failed');
      onApproved(session.id);
    } catch (err) {
      console.error('[ReviewCard] approve failed', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${ktsUrl}/api/review/${session.id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reason: rejectReason.trim() }),
      });
      if (!res.ok) throw new Error('Rejection failed');
      onRejected(session.id);
    } catch (err) {
      console.error('[ReviewCard] reject failed', err);
    } finally {
      setLoading(false);
      setRejecting(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-gray-200 overflow-hidden">
      {/* Header — always visible */}
      <button
        type="button"
        onClick={handleExpand}
        className="flex w-full items-center gap-3 p-4 text-left hover:bg-gray-50 transition-colors"
      >
        {/* Avatar */}
        {avatar ? (
          <img src={avatar} alt="" className="h-10 w-10 rounded-full object-cover shrink-0" />
        ) : (
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600 text-sm font-bold shrink-0">
            {(name[0] || '?').toUpperCase()}
          </div>
        )}

        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">{name}</p>
          <p className="text-xs text-gray-500">{session.exercise_name} — {sealedDate}</p>
        </div>

        {/* Rep count badge */}
        <div className="text-center shrink-0">
          <span className="text-2xl font-black text-blue-600 tabular-nums">{session.verified_reps}</span>
          <p className="text-[10px] text-gray-400">reps</p>
        </div>

        {/* Expand chevron */}
        <svg
          className={`h-5 w-5 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expanded view */}
      {expanded && (
        <div className="border-t border-gray-100 p-4 space-y-4">
          {/* Video player */}
          {videoUrl ? (
            <video
              src={videoUrl}
              controls
              playsInline
              className="w-full max-h-64 rounded-xl bg-black"
            />
          ) : session.video_url ? (
            <div className="flex items-center justify-center h-32 rounded-xl bg-gray-100 text-gray-400 text-sm">
              Loading video...
            </div>
          ) : (
            <div className="flex items-center justify-center h-32 rounded-xl bg-gray-100 text-gray-400 text-sm">
              No video recorded
            </div>
          )}

          {/* Action buttons */}
          {!rejecting ? (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleApprove}
                disabled={loading}
                className="flex-1 rounded-xl bg-green-600 py-2.5 text-sm font-bold text-white transition-colors hover:bg-green-700 disabled:opacity-50"
              >
                {loading ? 'Approving...' : 'Approve'}
              </button>
              <button
                type="button"
                onClick={() => setRejecting(true)}
                disabled={loading}
                className="flex-1 rounded-xl bg-red-50 py-2.5 text-sm font-bold text-red-600 transition-colors hover:bg-red-100 disabled:opacity-50"
              >
                Reject
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Reason for rejection (required)..."
                rows={2}
                className="w-full rounded-xl border border-gray-200 p-3 text-sm outline-none focus:border-red-300"
              />
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleReject}
                  disabled={loading || !rejectReason.trim()}
                  className="flex-1 rounded-xl bg-red-600 py-2 text-sm font-bold text-white disabled:opacity-50"
                >
                  {loading ? 'Rejecting...' : 'Confirm Reject'}
                </button>
                <button
                  type="button"
                  onClick={() => { setRejecting(false); setRejectReason(''); }}
                  className="rounded-xl bg-gray-100 px-4 py-2 text-sm text-gray-600"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
